import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ClipboardText, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import ScopeDocuments from "./ScopeDocuments";
import { Section, Field, ErrorBox, inputCls, errMsg, fmtDate } from "./shared";

export const SCOPE_FIELDS = [
  ["objective", "Objective", "What this project is meant to achieve."],
  ["deliverables", "Deliverables", "Concrete outputs to be handed over."],
  ["exclusions", "Exclusions", "What is explicitly out of scope."],
  ["timing", "Timing", "Phases, milestones and target dates."],
  ["responsibilities", "Responsibilities", "Who provides what (samples, data, approvals)."],
  ["success_criteria", "Success criteria", "How completion will be judged."],
  ["fee_terms", "Fee terms", "Internal notes on fees or billing basis."],
  ["confidentiality", "Confidentiality", "Handling of proprietary information."],
  ["change_control", "Change control", "How scope changes are requested and recorded."],
];

const toForm = (scope) => Object.fromEntries(SCOPE_FIELDS.map(([k]) => [k, scope?.[k] ?? ""]));

export default function ProjectScopePanel({ recipeId, scope, documents = [], onSaved }) {
  const [form, setForm] = useState(() => toForm(scope));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const loadedAt = useRef(scope?.updated_at ?? null);

  useEffect(() => {
    if (!dirty) { setForm(toForm(scope)); loadedAt.current = scope?.updated_at ?? null; }
  }, [scope, dirty]);

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.put(`/rd/recipes/${recipeId}/scope`, { ...form, expected_updated_at: loadedAt.current ?? null });
      loadedAt.current = data?.updated_at ?? loadedAt.current;
      setDirty(false);
      toast.success("Project scope saved");
      onSaved();
    } catch (err) {
      setError(`${errMsg(err)} Your edits are preserved below${err?.response?.status === 409 ? " — this scope was changed elsewhere; copy your edits, reload, and reapply" : ""}.`);
    } finally { setSaving(false); }
  };

  return (
    <Section icon={ClipboardText} title="Project scope" testId="rd-scope"
      subtitle={`Internal project scope — not a signed agreement. Planning notes only; no signatures or approvals are captured here.${scope?.updated_at ? ` Last saved ${fmtDate(scope.updated_at)}.` : " Not saved yet."}`}
      actions={
        <div className="flex items-center gap-2">
          {dirty && <span className="text-xs font-semibold text-[#9A7330]" data-testid="text-scope-unsaved">Unsaved changes</span>}
          {dirty && <Button size="sm" variant="ghost" onClick={() => setDirty(false)} data-testid="button-scope-discard">Discard</Button>}
          <Button size="sm" disabled={!dirty || saving} onClick={save} className="bg-[#17324D] hover:bg-[#22476B] text-white" data-testid="button-scope-save">
            <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save scope"}
          </Button>
        </div>
      }>
      <div className="mb-3 inline-block rounded border border-[#C69A4B]/50 bg-[#C69A4B]/10 px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#7A5C26]" data-testid="text-scope-disclaimer">
        Internal project scope — not a signed agreement
      </div>
      <ScopeDocuments recipeId={recipeId} documents={documents} onReload={onSaved} />
      <details>
      <summary className="cursor-pointer text-sm font-semibold text-[#17324D]">Optional planning notes</summary>
      <ErrorBox message={error} testId="error-scope" />
      <div className="grid sm:grid-cols-2 gap-3 mt-2">
        {SCOPE_FIELDS.map(([k, label, hint]) => (
          <Field key={k} label={label} hint={hint}>
            <textarea rows={3} className={inputCls} value={form[k]}
              onChange={(e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setDirty(true); }}
              data-testid={`input-scope-${k}`} />
          </Field>
        ))}
      </div>
      </details>
    </Section>
  );
}
